import * as React from 'react';
import {useContext, useEffect, useState} from 'react';
import { isFunction } from "../helpers/utils";
import DeleteButton from "../components/deleteButton";
import DialogStyled from "../components/dialogStyled";
import AssetInfoPill from "./assetInfoPill.tsx";
import MediaLibrary from "./mediaLibrary";
import { type AssetData, AssetImageFormat, AssetMode } from "./asset.interface";
import {ContextObjId} from "../state/contextObjId";
import {ContextUrls} from "../state/contextUrlApi";
import axios from "axios";
import BackgroundCheckered from "./backgroundCheckered";
import EmptyImage from "./emptyImage";
import {getImgUrl} from "./assetUtils";

export interface SelectAssetImageCommonProps {
	assetId: number,
	previewWidth?: number,
	previewHeight?: number,
	mode?: AssetMode,
	allowClear?: boolean,
	showModalOnRender?: boolean
}

interface Props extends SelectAssetImageCommonProps {
	onChange?: (asset?: AssetData) => void,
	format?: string,
	quality?: number,
	onChangeFormat?: (format?: AssetImageFormat) => void,
	onChangeQuality?: (quality?: number) => void,
}

function SelectAssetImage({
	assetId,
	mode = AssetMode.Thumb,
	previewWidth = 320,
	previewHeight = 240,
	allowClear = true,
	showModalOnRender = false,
	onChange,
	format,
	onChangeFormat,
	quality,
	onChangeQuality,
	...otherProps
}: Props) {
	const urls = useContext(ContextUrls);
	const objId = useContext(ContextObjId);
	const [asset, setAsset] = useState<AssetData|null>(null);
	const [showModal, setShowModal] = useState<boolean>(showModalOnRender);

	function fetchAsset(id: number) {
		return axios.get(urls.asset.url + '/' + id)
			.then((response) => {
				((newAsset: AssetData) => {
					setAsset(newAsset);
				})(response.data)
			})
			.catch(function (error) {
				console.log(error.config);
			});
	}

	useEffect(() => {
		if (assetId) {
			fetchAsset(assetId);
		}
	}, [assetId]);

	function closeModal() {
		setShowModal(false);
	}

	function openModal() {
		setShowModal(true);
	}

	function changeAsset(newAsset?: AssetData) {
		setAsset(newAsset);
		closeModal();
	}

	useEffect(() => {
		if (isFunction(onChange)) {
			onChange(asset);
		}
	}, [asset]);

	return (<>
		<div className="box relative shadow-inner rounded-sm max-w-[36rem] max-h-96 overflow-hidden">
			<div
				className="text-center cursor-pointer group"
				style={{width: previewWidth, height: previewHeight}}
				onClick={openModal}
			>
				{asset ? <>
					{allowClear && <DeleteButton
						onClick={(event)=> { event.stopPropagation(); changeAsset(null); }}
						className="absolute top-2 right-2 z-10"
					/>}
					{asset && asset.isImage && <>
						<div
							className="opacity-0 group-hover:opacity-100 ease-mine duration-300 transition-opacity absolute bottom-2 inset-x-2 text-sm z-10">
							<div className="flex flex-wrap gap-1 items-end">

								<AssetInfoPill className="break-keep">
									{asset.width}<span className="inline-block px-0.5 align-text-bottom">x</span>{asset.height}
								</AssetInfoPill>

								{format && <AssetInfoPill className="break-keep">
									{format}
								</AssetInfoPill>}

								<div className="grow"/>

								{isFunction(onChangeQuality) && format !== AssetImageFormat.Png &&
									<input
										type="number"
										name="quality"
										id="quality"
										placeholder="Quality"
										value={quality}
										className="!min-w-0 w-[4.1rem]"
										onChange={(event) => {
											let newQuality = parseInt(event.target.value);
											if (newQuality < 0) { newQuality = 0; }
											if (newQuality > 100) { newQuality = 100; }
											if (isFunction(onChangeQuality)) {
												onChangeQuality(newQuality);
											}
										}}
										onClick={(event) => { event.stopPropagation(); }}
									/>
								}

								{isFunction(onChangeFormat) && <select
									value={format}
									className="!min-w-0"
									onChange={(event) => {
										event.stopPropagation();
										if (isFunction(onChangeFormat)) {
											// @ts-ignore
											onChangeFormat(event.target.value);
										}
									}}
									onClick={(event) => { event.stopPropagation(); }}
								>
									<option value={AssetImageFormat.Jpg}>{AssetImageFormat.Jpg}</option>
									<option value={AssetImageFormat.Png}>{AssetImageFormat.Png}</option>
								</select>}
							</div>
						</div>

						<BackgroundCheckered className="h-full z-0">
							<img src={getImgUrl(mode, asset?.id, asset?.filename, previewWidth, previewHeight, quality ? quality : 70)} alt=""
							     className="max-h-96 mx-auto"/>
						</BackgroundCheckered>
					</>}
				</> : <span className="text-grey-400"><EmptyImage/></span>}
			</div>
		</div>

		{/*<Dump value={...assetId}/>*/}

		<DialogStyled
			isOpen={showModal}
			onClose={closeModal}
		>
			<div className="p-8">
				<MediaLibrary
					objectId={objId}
					uploadEnabled={true}
					editable={true}
					onSelect={changeAsset}
				/>
			</div>
		</DialogStyled>
	</>);
}

export default SelectAssetImage;
